import type { IntersectionQuery, IntersectionQueryResult } from "@speckle/viewer";

// The SDK only raycasts the literal cursor pixel and its click handler trusts that raycast's last
// result, so a cursor one pixel past a building's silhouette finds nothing and the click is a no-op.
const RING_RADII_PX = [3, 6, 9, 12];
const RING_DIRECTIONS = 8;

// Structural, not the SDK's `Viewer` class: that instance flows through Pinia's storeToRefs/shallowRef,
// which strips its private-field nominal typing before it reaches this module.
export interface PickableViewer {
  getContainer(): HTMLElement;
  query(query: IntersectionQuery): IntersectionQueryResult | null;
}

export const toNdc = (clientX: number, clientY: number, rect: { left: number; top: number; width: number; height: number }) => ({
  x: ((clientX - rect.left) / rect.width) * 2 - 1,
  y: ((clientY - rect.top) / rect.height) * -2 + 1,
});

// Nearest-first outward ring search around a missed pixel. Pure so the search order is unit-testable
// without a real viewer or DOM.
export const findNearbyHit = (
  hasHitAt: (clientX: number, clientY: number) => boolean,
  clientX: number,
  clientY: number
): { clientX: number; clientY: number } | null => {
  for (const radius of RING_RADII_PX) {
    for (let i = 0; i < RING_DIRECTIONS; i++) {
      const angle = (i / RING_DIRECTIONS) * Math.PI * 2;
      const x = clientX + Math.cos(angle) * radius;
      const y = clientY + Math.sin(angle) * radius;
      if (hasHitAt(x, y)) return { clientX: x, clientY: y };
    }
  }
  return null;
};

let installedContainer: HTMLElement | null = null;
let installedViewer: PickableViewer | null = null;
let frameLocked = false;
// Only search near a fresh edge crossing (hit a moment ago, missing now) — searching on every move while
// the cursor roams open space away from the model would cost a ring of raycasts for nothing.
let lastMoveHit = true;

const hasHit = (viewer: PickableViewer, clientX: number, clientY: number, rect: DOMRect): boolean => {
  const query: IntersectionQuery = { operation: "Pick", point: toNdc(clientX, clientY, rect) };
  return !!viewer.query(query)?.objects?.length;
};

const redispatch = (container: HTMLElement, source: PointerEvent, clientX: number, clientY: number) => {
  container.dispatchEvent(
    new PointerEvent(source.type, {
      bubbles: true,
      cancelable: true,
      pointerId: source.pointerId,
      pointerType: source.pointerType,
      isPrimary: source.isPrimary,
      button: source.button,
      buttons: source.buttons,
      clientX,
      clientY,
    })
  );
};

// Capture-phase, mirroring pick-guard.ts's blockHover listener: it must see the pointermove before the
// SDK's own bubble-phase handler does, so a corrected redispatch can replace it rather than race it.
const handlePointerMove = (event: PointerEvent) => {
  const viewer = installedViewer;
  const container = installedContainer;
  if (!viewer || !container || frameLocked) return;
  // Must be set before the redispatch below: the synthetic pointermove re-enters this same listener
  // (same container, same phase), and only this early flag stops that from recursing.
  frameLocked = true;
  requestAnimationFrame(() => {
    frameLocked = false;
  });

  const rect = container.getBoundingClientRect();
  if (hasHit(viewer, event.clientX, event.clientY, rect)) {
    lastMoveHit = true;
    return;
  }
  if (!lastMoveHit) return;

  const corrected = findNearbyHit((x, y) => hasHit(viewer, x, y, rect), event.clientX, event.clientY);
  // Keep searching next move only if this one found something nearby — a miss the ring search also
  // misses means the cursor has genuinely left the model, not just drifted off an edge.
  lastMoveHit = !!corrected;
  if (!corrected) return;

  event.stopImmediatePropagation();
  event.preventDefault();
  redispatch(container, event, corrected.clientX, corrected.clientY);
};

export const installPickAssist = (viewer: PickableViewer) => {
  const container = viewer.getContainer();
  if (installedContainer === container) return;
  if (installedContainer) uninstallPickAssist();
  container.addEventListener("pointermove", handlePointerMove, true);
  installedContainer = container;
  installedViewer = viewer;
  lastMoveHit = true;
};

export const uninstallPickAssist = () => {
  if (!installedContainer) return;
  installedContainer.removeEventListener("pointermove", handlePointerMove, true);
  installedContainer = null;
  installedViewer = null;
};
